<script>
import baseMixin from '@/mixins/baseMixin'
import { mapActions, mapGetters } from 'vuex'
import dayjs from 'dayjs'
import DateFormatToken from '@/constants/date/DateFormatToken'
import truncateText from '@utils/truncate-text'
import LocationRestriction from '@/models/locationRestriction/locationRestriction'
import RestrictionStatus from '@/constants/locationRestriction/RestrictionStatus'
import VuetifyColourClass from '@/constants/core/VuetifyColourClass'

export default {
  name: 'LocationRestrictionList',
  filters: {
    truncateText,
  },
  mixins: [baseMixin],
  props: {
    search: {
      type: String,
      default: null,
    },
    adminIds: {
      type: Array,
      default: () => [],
    },
  },
  data: () => {
    return {
      RestrictionStatus,
      headers: [
        {
          text: 'Status',
          value: 'status',
        },
        {
          text: 'Client',
          value: 'clientName',
        },
        {
          text: 'Consultant',
          value: 'ownerName',
        },
        {
          text: 'Location',
          value: 'locationName',
        },
        {
          text: 'Start',
          value: 'dateFrom',
        },
        {
          text: 'End',
          value: 'dateUntil',
        },
        {
          text: 'Message',
          value: 'message',
        },
        {
          value: 'actions',
          sortable: false,
        },
      ],
    }
  },
  computed: {
    ...mapGetters({
      isLoading: 'locationRestriction/isLoading',
      locationRestrictions: 'locationRestriction/restrictions',
      locationRestrictionsForAdmin:
        'locationRestriction/restrictionsByAdminIds',
      getAdminById: 'staff/getAdminById',
      locationRestrictionsByLocationId:
        'locationRestriction/restrictionsByLocationId',
    }),

    creationRestrictions() {
      return this.locationRestrictionsForAdmin(this.adminIds).filter(
        (a) => a.restriction === 'booking.create'
      )
    },
  },
  watch: {
    async adminId() {
      await this.loadLocationAlerts(this.adminId)
    },
  },
  async created() {
    await this.loadLocationAlerts(this.adminId)
  },
  methods: {
    ...mapActions({
      loadLocationAlerts:
        'locationRestriction/loadAllActiveAndUpcomingRestrictions',
    }),
    formatDate(d) {
      return dayjs(d).locale(this.$i18n.locale).format(DateFormatToken.ll)
    },
    handleExtendClick(block) {
      this.$emit('extend', new LocationRestriction(block))
    },
    handleRemoveClick(block) {
      this.$emit('remove', new LocationRestriction(block))
    },
    status(restriction) {
      return {
        color: () => {
          switch (restriction.status) {
            case RestrictionStatus.ACTIVE:
              return VuetifyColourClass.success
            case RestrictionStatus.PAST:
              return VuetifyColourClass.orange
            case RestrictionStatus.UPCOMING:
              return VuetifyColourClass.purple
          }
        },
      }
    },
  },
}
</script>

<template>
  <div>
    <v-data-table
      class="custom-elevation rounded-xl"
      :loading="isLoading"
      :headers="headers"
      :items="creationRestrictions"
      :search="search"
    >
      <template v-slot:item.status="{ item }">
        <v-chip :color="status(item).color()" small outlined pill>{{
          item.status
        }}</v-chip>
      </template>

      <template v-slot:item.clientName="{ item }">
        {{ item.clientName }}
      </template>

      <template v-slot:item.ownerName="{ item }">
        {{ getAdminById(item.ownerId).name }}
      </template>

      <template v-slot:item.locationName="{ item }">
        {{ item.locationName }}
      </template>

      <template v-slot:item.dateFrom="{ item }">
        {{ formatDate(item.dateFrom) }}
      </template>

      <template v-slot:item.dateUntil="{ item }">
        <div class="d-flex align-center">
          {{ formatDate(item.dateUntil) }}
        </div>
      </template>

      <template v-slot:item.message="{ item }">
        <v-tooltip bottom max-width="400px">
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              {{ item.message | truncateText(50) }}
            </span>
          </template>
          <span>{{ item.message }}</span>
        </v-tooltip>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-menu rounded offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn depressed icon v-bind="attrs" v-on="on">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item link @click="handleExtendClick(item)">
              <v-list-item-title>{{
                $t('locationRestrictions.list.extendTooltip')
              }}</v-list-item-title>
            </v-list-item>
            <v-list-item link @click="handleRemoveClick(item)">
              <v-list-item-title>{{
                $t('locationRestrictions.list.finishLabel')
              }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
  </div>
</template>
